import { Link } from "react-router-dom";

import { Layout } from "@/components/layout";
import { usePosts } from "@/lib/queries/use-posts";
import { useProjects } from "@/lib/queries/use-projects";
import { useSiteContent } from "@/lib/queries/use-site-content";
import { useSocialLinks } from "@/lib/queries/use-social-links";

function formatDate(dateString: string | null): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" }).toUpperCase();
}

export function Home() {
  const { data: posts } = usePosts();
  const { data: projects } = useProjects();
  const { data: content } = useSiteContent();
  const { data: socialLinks } = useSocialLinks();

  // Get the 3 most recent posts
  const recentPosts = posts?.slice(0, 3) ?? [];
  // Get the first 2 projects for the homepage
  const displayProjects = projects?.slice(0, 2) ?? [];

  // Content with defaults
  const tagline = content?.home_tagline || "Trying to be useful.";
  const description =
    content?.home_description ||
    'Previously at <span class="text-primary">Vista Power</span> (YC W24), <span class="text-primary">SpaceX</span>, <span class="text-primary">Rocket Lab</span>, and <span class="text-primary">Tour</span> (acq).';
  const educationLine = content?.home_education_line || "Economics & Physics @ Cal, '23.";

  return (
    <Layout>
      {/* Hero Section */}
      <section className="mx-auto max-w-4xl px-8 pt-20 pb-16 border-b border-border">
        <p className="text-[11px] text-text-muted tracking-wide-caps uppercase mb-6">Maxim Kraft</p>
        <h1 className="text-[44px] font-bold text-primary leading-[1.15] mb-8 tracking-tight max-w-lg">
          {tagline}
        </h1>
        <div className="flex gap-16">
          <div className="flex-1">
            <div
              className="text-[15px] text-text-secondary leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:text-primary [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:opacity-80"
              dangerouslySetInnerHTML={{ __html: description }}
            />
            <p className="text-[14px] text-text-tertiary mt-2">{educationLine}</p>
          </div>
          <div className="flex gap-6">
            {socialLinks?.slice(0, 2).map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12px] text-text-muted hover:text-primary tracking-wider"
              >
                {link.label} ↗
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Writing Section */}
      <section className="mx-auto max-w-4xl px-8 py-14">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="text-[11px] text-text-muted font-bold tracking-wide-caps uppercase">
            Writing
          </h2>
          <Link
            to="/writing"
            className="text-[11px] text-text-faint hover:text-primary tracking-wider uppercase"
          >
            All posts →
          </Link>
        </div>

        {recentPosts.length > 0 ? (
          <div className="border-t border-border">
            {recentPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/writing/${post.slug}`}
                className="group flex items-baseline justify-between py-5 border-b border-border hover:bg-card -mx-4 px-4 transition-colors"
              >
                <div className="flex-1">
                  <h3 className="text-[17px] font-bold text-text-primary group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  {post.excerpt && (
                    <p className="text-[13px] text-text-tertiary mt-1">{post.excerpt}</p>
                  )}
                </div>
                <time className="text-[11px] text-text-faint tracking-wider ml-8 shrink-0">
                  {formatDate(post.published_at)}
                </time>
              </Link>
            ))}
          </div>
        ) : (
          <div className="border-t border-border py-8">
            <p className="text-text-muted text-center text-[14px]">
              No posts yet. Check back soon.
            </p>
          </div>
        )}
      </section>

      {/* Projects Section */}
      <section className="mx-auto max-w-4xl px-8 py-14 border-t border-border">
        <h2 className="text-[11px] text-text-muted font-bold tracking-wide-caps uppercase mb-8">
          Projects
        </h2>

        {displayProjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {displayProjects.map((project) => {
              const isExternal = project.url?.startsWith("http");
              return (
                <a
                  key={project.slug}
                  href={project.url || "#"}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noopener noreferrer" : undefined}
                  className="group block p-6 border border-border hover:border-border-hover transition-all bg-card"
                >
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-[10px] text-text-faint tracking-caps uppercase">
                      {project.category || "Project"}
                    </p>
                    <span className="text-text-faint group-hover:text-text-tertiary transition-colors">
                      ↗
                    </span>
                  </div>
                  <h3 className="text-[17px] font-bold text-text-secondary group-hover:text-primary transition-colors mb-2">
                    {project.title}
                  </h3>
                  {project.description && (
                    <p className="text-[13px] text-text-tertiary">{project.description}</p>
                  )}
                </a>
              );
            })}
          </div>
        ) : (
          <div className="py-8">
            <p className="text-text-muted text-center text-[14px]">
              No projects yet. Check back soon.
            </p>
          </div>
        )}
      </section>
    </Layout>
  );
}
